import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import GoBackButton from '@/common/components/GoBackButton'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import TextField from '@/common/components/TextField'
import { useGoBack } from '@/common/hooks/useGoBack'
import useUserPermissions from '@/common/hooks/useUserPermissions'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useCreateEstablishmentTechnicalProfessional } from '@/features/maintenance/establishmentTechnicalProfessionals/hooks/useCreateEstablishmentTechnicalProfessional'
import {
  establishmentTechnicalProfessionalCreateSchema,
  EstablishmentTechnicalProfessionalValidationSchema,
} from '@/features/maintenance/establishmentTechnicalProfessionals/schemas/create.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { QueryClient } from '@tanstack/react-query'
import { ChevronsRightIcon } from 'lucide-react'
import { useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

const Create = () => {
  const goBack = useGoBack()
  const navigate = useNavigate()
  const queryClient = new QueryClient()

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()

  const userHasPermissionToCreateEstablishmentTechnicalProfessional =
    isUserPermissionsPending || hasUserPermission('Admin::Maintenance::EstablishmentTechnicalProfessionals::Create')

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<EstablishmentTechnicalProfessionalValidationSchema>({
    resolver: zodResolver(establishmentTechnicalProfessionalCreateSchema),
  })

  const {
    mutate: createEstablishmentTechnicalProfessional,
    isPending: createEstablishmentTechnicalProfessionalIsPending,
  } = useCreateEstablishmentTechnicalProfessional({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['establishmentTechnicalProfessionals'] })
      navigate('/establishment-technical-professionals')
    },
    onError: FormTransformers.errorsTransformer({ setError, setMessages }),
  })

  const onSubmit: SubmitHandler<EstablishmentTechnicalProfessionalValidationSchema> = (data) =>
    createEstablishmentTechnicalProfessional(data)

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-128px)]"
      statusCode={userHasPermissionToCreateEstablishmentTechnicalProfessional ? 200 : 403}
    >
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="false" className="flex flex-col gap-y-6">
        <div className="flex justify-between items-center">
          <div className="flex gap-2 items-center">
            <GoBackButton fallback="/establishment-technical-professionals" />

            <p className="text-white-400 font-normal">Especialidade Técnica</p>

            <ChevronsRightIcon className="text-white-400" />

            <h2>Nova Especialidade</h2>
          </div>

          <div className="flex gap-4">
            <Button
              variant="outline-error"
              onClick={() => goBack({ fallback: '/establishment-technical-professionals' })}
            >
              Cancelar
            </Button>

            <Button
              type="submit"
              name="create_establishment_technical_professional"
              className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
              disabled={createEstablishmentTechnicalProfessionalIsPending}
              isLoading={createEstablishmentTechnicalProfessionalIsPending}
            >
              Criar Especialidade
            </Button>
          </div>
        </div>

        <Box className="max-h-[calc(100vh-128px)] flex flex-col gap-4 overflow-y-auto">
          <p className="text-lg font-bold">Especialidade Técnica</p>

          <TextField
            label="Nome"
            markAsRequired
            {...register('display_name')}
            errorMessage={errors?.display_name?.message}
            disabled={createEstablishmentTechnicalProfessionalIsPending}
          />
        </Box>

        {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
      </form>
    </ErrorMessageWrapper>
  )
}

export default Create
